<template>
  <div>
    <confirm-modal name="retry_import_confirm" :title="`Retry ${getFeedLabel(true)} Import`">
      <p>Retrying the import will delete the {{ getFeedLabel(true) }}, any {{ getItemLabel(true) }}s that have been created and any Storage Assets that were uploaded.</p>
      <p>The {{ getFeedLabel() }} import will then reattempt from scratch.</p>
      <p>Would you like to continue ?</p>
    </confirm-modal>

    <confirm-modal name="abandon_import_confirm" :title="`Abandon ${getFeedLabel(true)} Import`">
      <p>Abandoning the import will delete the {{ getFeedLabel(true) }}, any {{ getItemLabel(true) }}s that have been created and any Storage Assets that were uploaded.</p>
      <p>Would you like to continue ?</p>
    </confirm-modal>

    <vjs-modal name="item_error_modal" title="Import Failed">
      <template #body-content>
        <b-row>
          <b-col v-if="item_error_modal_data">
            <p>{{ item_error_modal_data.public_error }}</p>
            <p>
              Please review your {{ getFeedLabel() }} XML and try again, or contact SharpStream Support
            </p>
            <hr>
            <vue-json-pretty :data="item_error_modal_data.item_data" />
          </b-col>
        </b-row>
      </template>
      <template #body-controls>
        <b-button variant="ss" @click="() => $modal.hide('item_error_modal')">
          Close
        </b-button>
      </template>
    </vjs-modal>

    <div class="row">
      <div class="col-12">
        <basic-portlet :title="portlet_title" icon="fa fa-rss">
          <div class="row">
            <div class="col-12 col-md-4 col-md-pull-4 mb-4">
              <h5 class="mb-4">
                <span>Status :</span>
                <span>{{ getStatusText(live_feed_import_data.status) }}</span>
              </h5>
              <div v-if="import_pending || import_processing" class="progress-holder mb-4">
                <span class="text-center"><strong>Import Progress : {{ import_progress_percentage }}%</strong></span>
                <b-progress :value="import_progress" :max="import_max_progress"
                            :animated="import_progress !== import_max_progress"
                            height="20px" />
              </div>
              <a v-if="import_complete && live_feed_import_data.feed_id" :href="`/feeds/${live_feed_import_data.feed.uuid}/edit`"
                 target="_blank"
                 class="btn btn-ss-standout">View New {{ getFeedLabel(true) }}</a>
              <button v-if="import_failed || import_partially_complete" class="btn btn-ss" @click="retryFeedImport">
                Retry Import
              </button>
              <button v-else-if="import_complete" class="btn btn-ss" @click="retryFeedImport">
                Reimport {{ getFeedLabel(true) }}
              </button>

              <button v-if="import_partially_complete || import_failed" class="btn btn-ss-danger" @click="abandon">
                Abandon Import
              </button>

              <div v-if="live_feed_import_data.status === 3" class="card border-danger mt-4">
                <div class="card-header text-ss-danger">
                  <strong>{{ getFeedLabel(true) }} Creation Failed</strong>
                </div>
                <div class="card-body">
                  <p>
                    Your {{ getItemLabel(true) }}s have been processed but we were unable to create your {{ getFeedLabel(true) }}.
                  </p>
                  <p>
                    Reason : {{ live_feed_import_data.public_error }}
                  </p>
                  <p>
                    Please review your {{ getFeedLabel(true) }} XML and try again. If you continue to have issues, please contact SharpStream Support.
                  </p>
                  <vue-json-pretty :data="item_error_modal_data.feed_data" />
                </div>
              </div>

              <div v-if="has_unexpected_error" class="card border-danger mt-4">
                <div class="card-header text-ss-danger">
                  <strong>Unexpected Error</strong>
                </div>
                <div class="card-body">
                  <p>
                    Some of your {{ getItemLabel(true) }}s have failed for an unexpected reason, This can sometimes be caused by exceptionally large files.
                    The "Failed" button will provide you with more information about each failed {{ getItemLabel(true) }}. Click "Retry" on the
                    {{ getItemLabel(true) }} when you have resolved the problem.
                    You can still create your {{ getFeedLabel(true) }} by clicking the below button, but it will not include the failed
                    {{ getItemLabel(true) }}s.
                  </p>
                  <p>
                    If you continue to have issues, please contact SharpStream Support.
                  </p>
                  <button class="btn btn-ss" @click="createFeed">
                    Create {{ getFeedLabel(true) }}
                  </button>
                </div>
              </div>

              <div v-if="failed_items.length && import_complete" class="card border-danger mt-4">
                <div class="card-header text-ss-danger">
                  <strong>{{ getFeedLabel(true) }} Partially Created</strong>
                </div>
                <div class="card-body">
                  <p>
                    Your {{ getFeedLabel() }} has been created, but some of your {{ getItemLabel(true) }}s have not been imported. Please review the table and attempt to rectify the issue. The "Failed" button will provide you with more information about each failed Item. Click "Retry" on the Item when you have resolved the problem.
                  </p>
                  <p>
                    If you opt to replace the {{ getItemLabel(true) }} or add any new {{ getItemLabel(true) }}s, please click the "Reimport
                    {{ getFeedLabel(true) }}" button.
                  </p>
                  <p>
                    If you continue to have issues, please contact SharpStream Support.
                  </p>
                </div>
              </div>
              <div v-if="import_complete" class="card mt-4">
                <div class="card-header">
                  <strong>Additional Information</strong>
                </div>
                <div class="card-body">
                  <div v-if="new_feed_xml">
                    <p>
                      Your new {{ getFeedLabel(true) }} XML url is : <a :href="new_feed_xml" target="_blank">{{ new_feed_xml }}</a>
                    </p>
                    <p>We recommend that you update the 'new_feed_url' property of your old XML so that the old {{ getFeedLabel(true) }} redirects to the new feed.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-8">
              <b-table striped
                       bordered
                       :items="live_feed_import_data.feed_import_items"
                       :fields="feed_import_item_fields">
                <template #cell(status)="data">
                  <button v-if="data.item.status === 3" :class="getStatusButtonClass(data.item.status)" @click="fireErrorQuickAlert(data.item)">
                    {{ getStatusText(data.item.status, true) }} <span class="fa fa-info-circle" />
                  </button>
                  <button v-else :class="getStatusButtonClass(data.item.status)">
                    {{ getStatusText(data.item.status, true) }}
                  </button>
                </template>
                <template #cell(storage_id)="data">
                  <span v-if="data.item.storage_id">
                    <span v-if="!data.item.storage">No longer exists...</span>
                    <a v-else :href="`/storage/${data.item.storage.uuid}/edit`"
                       target="_blank"
                       class="btn btn-ss">View</a>
                  </span>
                  <span v-else-if="data.item.status === 3" class="ss-danger">---</span>
                  <span v-else>Pending...</span>
                </template>
                <template #cell(item_id)="data">
                  <span v-if="data.item.item_id">
                    <span v-if="!data.item.item">No longer exists...</span>
                    <a v-else :href="`/${isPodcaster() ? 'episode' : 'items'}/${data.item.item.uuid}/edit`"
                       target="_blank"
                       class="btn btn-ss">View</a>
                  </span>
                  <span v-else-if="data.item.status === 3" class="ss-danger">---</span>
                  <span v-else>Pending...</span>
                </template>
                <template #cell(id)="data">
                  <button v-if="data.item.status === 3" class="btn btn-ss" @click="retryItem(data.item)">
                    Retry
                  </button>
                </template>
              </b-table>
            </div>
          </div>
        </basic-portlet>
      </div>
    </div>
  </div>
</template>

<script>

import BasicPortlet from "VueComponents/BasicPortlet";
import { BProgress, BTable } from "bootstrap-vue";
import VjsModal from "VueModals/VjsModal";
import VueJsonPretty from "vue-json-pretty";
import 'vue-json-pretty/lib/styles.css';
import ConfirmModal from "VueModals/ConfirmModal";

export default {
  components: {
    BasicPortlet,
    BTable,
    VjsModal,
    VueJsonPretty,
    BProgress,
    ConfirmModal,
  },
  data() {
    return {
      feed_import: window.feed_import,
      feed_import_item_fields: [
        { key: 'item_data.name', label: 'Name' },
        { key: 'status', label: 'Import Status' },
        { key: 'storage_id', label: 'Storage Asset' },
        { key: 'item_id', label: this.getItemLabel(true) },
        { key: 'id', label: 'Actions' },

      ],
      item_error_modal_data: null,
    };
  },
  computed: {
    portlet_title() {
      return `Import : ${this.feed_import.feed_data.name}`;
    },
    live_feed_import_data() {
      return this.feed_import;
    },
    import_progress_percentage() {
      return Math.round((this.import_progress / this.import_max_progress) * 100);
    },
    import_progress() {
      let progress = 0;
      _.forEach(this.live_feed_import_data.feed_import_items, (item) => {
        // If the item is processing then the item is half complete.
        if (item.status === 1) {
          progress += 1;
        }

        // If complete or failed then the the item has finished processing
        if ([2, 3].includes(item.status)) {
          progress += 2;
        }
      });
      return progress;
    },
    import_max_progress() {
      // Give each of the items a value of 2. It's later awarded 1 point if in progress and 2 if complete.
      return this.feed_import.feed_import_items.length * 2;
    },
    failed_items() {
      return _.filter(this.live_feed_import_data.feed_import_items, {
        'status': 3,
      });
    },
    import_failed() {
      return this.live_feed_import_data.status === 3;
    },
    import_complete() {
      return this.live_feed_import_data.status === 2;
    },
    import_partially_complete() {
      return this.import_complete && this.failed_items.length;
    },
    import_successful() {
      return this.import_complete && !this.failed_items.length;
    },
    import_pending() {
      return this.live_feed_import_data.status === 0;
    },
    import_processing() {
      return this.live_feed_import_data.status === 1;
    },
    new_feed_xml() {
      return this.live_feed_import_data.feed?.feed_xml_file_url;
    },
    all_items_finished() {
      let all_finished = true;
      _.forEach(this.live_feed_import_data.feed_import_items, (item) => {
        if (item.status === 1 || item.status === 0) {
          all_finished = false;
        }
      });
      return all_finished;
    },
    has_unexpected_error() {
      return !this.live_feed_import_data.feed_id && this.all_items_finished;
    },
  },
  mounted() {
    this.updateFeedImportInfo();
  },
  methods: {
    updateFeedImportInfo() {
      axios.get(`/api/v1/feed-importer/${this.feed_import.uuid}`).then((response) => {
        this.feed_import = response.data.data;
        setTimeout(() => {
          this.updateFeedImportInfo();
        }, 5000);
      }).catch((error) => {
        // do nothing.
        console.error(error);
      });
    },
    retryItem(feedImportItem) {
      axios.patch(`/api/v1/feed-importer-item/${feedImportItem.uuid}/retry`).then(() => {
        toastr.success(`Retrying ${this.getItemLabel(true)}.`);
      }).catch(() => {
        toastr.error("Unable to retry item, please contact SharpStream Support.");
      });
    },
    createFeed() {
      axios.post(`/api/v1/feed-importer/${this.feed_import.uuid}/create-feed`).then(() => {
        toastr.success("Forcefully creating Feed.");
      }).catch((error) => {
        this.handleAxiosError(error);
      });
    },
    async retryFeedImport() {
      const modal_res = await this.fireModal('retry_import_confirm');
      if (modal_res.data.confirm) {
        axios.patch(`/api/v1/feed-importer/${this.feed_import.uuid}/retry`).then(() => {
          toastr.success("Retrying Feed.");
        }).catch(() => {
          toastr.error("Unable to retry, please contact SharpStream Support.");
        });
      }
    },
    async abandon() {
      const modal_res = await this.fireModal("abandon_import_confirm");
      if (modal_res.data.confirm) {
        axios.delete(`/api/v1/feed-importer/${this.feed_import.uuid}/abandon`).then(() => {
          toastr.success("Import abandoned. Redirecting...");
          window.location.href = "/feeds";
        }).catch(() => {
          toastr.error("Unable to abandon feed import, please contact SharpStream Support");
        });
      }
    },
    getStatusText(status, item = false) {
      switch (status) {
        case 1:
          return 'Processing';
        case 2:
          return item ? 'Success' : 'Complete';
        case 3:
          return 'Failed';
        default:
          return 'Pending';
      }
    },
    getStatusButtonClass(status) {
      switch (status) {
        case 1:
          return 'btn btn-ss kt-spinner kt-spinner--v2 kt-spinner--sm kt-spinner--dark unclickable';
        case 2:
          return 'btn btn-success unclickable';
        case 3:
          return 'btn btn-ss-danger';
        default:
          return 'btn btn-ss unclickable';
      }
    },
    fireErrorQuickAlert(item) {
      this.item_error_modal_data = item;
      this.fireModal(`item_error_modal`);
    },
  },
};
</script>

<style lang="scss" scoped>
.btn {
  &.unclickable {
    cursor: default !important;
    pointer-events: none !important;
  }
}
</style>
